import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isCsvFile } from '../shared/csvFileValidation';
import { readCsvFile } from '../shared/readCsvFile';

interface Props {
  ou: string;
  onOuChange: (ou: string) => void;
  onUploaded: (csv: string, fileName: string) => void;
  onBack: () => void;
}

/**
 * Paso 2 del wizard de Usuarios: región (combo) + selección/drag&drop del CSV.
 *
 * La región ya NO se escribe por fila en el CSV — se elige aquí, una sola vez por archivo, porque
 * escribirla a mano en el Excel generaba errores de tipeo/idioma ("Europe" vs "Europa", etc.) que
 * el backend rechazaba fila por fila. El parseo real del CSV lo hace el backend.
 */
export function StepUploadFile({ ou, onOuChange, onUploaded, onBack }: Readonly<Props>) {
  const { t } = useTranslation('recognition');
  const fileRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState('');
  const [fileError, setFileError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleFile = async (file: File) => {
    setFileError(null);
    if (!ou) {
      setFileError(t('admin.bulkUsers.ouRequired'));
      return;
    }
    if (!isCsvFile(file)) {
      setFileError(t('bulk.invalidFileType'));
      return;
    }

    try {
      const text = await readCsvFile(file);
      if (text.trim().length === 0) {
        setFileError(t('bulk.emptyFile'));
        return;
      }
      setFileName(file.name);
      onUploaded(text, file.name);
    } catch {
      setFileError(t('bulk.parseError'));
    }
  };

  return (
    <div className="bulk-step bulk-step--upload">
      <div className="bulk-ou-field">
        <label htmlFor="admin-bulk-users-ou">{t('admin.bulkUsers.ouLabel')}</label>
        <select
          id="admin-bulk-users-ou"
          data-testid="admin-bulk-users-ou-select"
          value={ou}
          onChange={(event) => onOuChange(event.target.value)}
        >
          <option value="">{t('admin.bulkUsers.ouPlaceholder')}</option>
          <option value="EUROPA">{t('admin.bulkUsers.ouEuropa')}</option>
          <option value="USA">{t('admin.bulkUsers.ouUsa')}</option>
        </select>
        <p className="bulk-ou-hint">{t('admin.bulkUsers.ouHint')}</p>
      </div>

      <button
        type="button"
        data-testid="admin-bulk-users-dropzone"
        className={`bulk-dropzone ${isDragOver ? 'bulk-dropzone--over' : ''}`}
        disabled={!ou}
        onClick={() => fileRef.current?.click()}
        onDragOver={(event) => { event.preventDefault(); setIsDragOver(true); }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={(event) => {
          event.preventDefault();
          setIsDragOver(false);
          const file = event.dataTransfer.files?.[0];
          if (file) void handleFile(file);
        }}
      >
        <i className="fas fa-file-csv" />
        <p>{t('bulk.dragDropHint')}</p>
        {fileName && <span className="bulk-filename">{fileName}</span>}
      </button>
      <input
        ref={fileRef}
        data-testid="admin-bulk-users-file-input"
        type="file"
        accept=".csv,text/csv"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void handleFile(file);
          event.target.value = '';
        }}
      />

      {fileError && <p className="rp-error" role="alert"><i className="fas fa-triangle-exclamation" /> {fileError}</p>}

      <div className="bulk-wizard-nav">
        <button type="button" className="btn btn--secondary" onClick={onBack}>
          <i className="fas fa-arrow-left" /> {t('bulk.back')}
        </button>
        <button type="button" className="btn btn--secondary" disabled={!ou} onClick={() => fileRef.current?.click()}>
          <i className="fas fa-upload" /> {t('bulk.upload')}
        </button>
      </div>
    </div>
  );
}

export default StepUploadFile;
